/**
 * lunar.js — 农历（夏历）转换
 *
 * 梅花易数的时间起卦用的是**农历**年月日时，不是公历，也不是干支月。
 * 农历的月首由朔（新月）时刻决定、闰月由无中气规则决定，
 * 用天文公式实时算会在个别年份差一天（新月正好压在午夜附近时）。
 *
 * 所以这里不算，直接查表：表由寿星天文历离线生成，1900–2100 共 201 年，
 * 打包后 3KB，运行时零计算、零误差。
 */

import table from './lunar-table.json' with { type: 'json' };
import { toJDN, BRANCHES } from './ganzhi.js';

const BASE = table.base;
const YEARS = table.years; // [ [正月初一JDN, packed], ... ]

/** packed: bit0–12 各月是否 30 天，bit13–16 闰月号（0 为无闰） */
const leapOf = (packed) => (packed >> 13) & 0xf;
const monthLen = (packed, i) => ((packed >> i) & 1 ? 30 : 29);

export const LUNAR_RANGE = { from: BASE, to: BASE + YEARS.length - 1 };

/**
 * 公历 → 农历。
 * @returns { year, month, day, isLeap, monthName, dayName }
 */
export function toLunar(year, month, day) {
  const jdn = toJDN(year, month, day);
  let li = year - BASE;
  if (li < 0 || li >= YEARS.length) throw new Error(`农历表只覆盖 ${LUNAR_RANGE.from}–${LUNAR_RANGE.to}`);
  if (jdn < YEARS[li][0]) li -= 1; // 还没到正月初一，属上一农历年
  if (li < 0) throw new Error('超出农历表范围');

  const [start, packed] = YEARS[li];
  const leap = leapOf(packed);
  let offset = jdn - start;
  const count = leap ? 13 : 12;

  let index = 0;
  while (index < count) {
    const len = monthLen(packed, index);
    if (offset < len) break;
    offset -= len;
    index += 1;
  }
  if (index >= count) throw new Error('农历表数据不足');

  // index 是「第几个月」，要换算成月号 + 是否闰
  let m;
  let isLeap = false;
  if (!leap || index < leap) {
    m = index + 1;
  } else if (index === leap) {
    m = leap;
    isLeap = true;
  } else {
    m = index;
  }

  return {
    year: BASE + li,
    month: m,
    day: offset + 1,
    isLeap,
    monthName: (isLeap ? '闰' : '') + MONTH_NAMES[m - 1],
    dayName: DAY_NAMES[offset]
  };
}

const MONTH_NAMES = ['正', '二', '三', '四', '五', '六', '七', '八', '九', '十', '冬', '腊'];
const DAY_NAMES = [
  '初一', '初二', '初三', '初四', '初五', '初六', '初七', '初八', '初九', '初十',
  '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十',
  '廿一', '廿二', '廿三', '廿四', '廿五', '廿六', '廿七', '廿八', '廿九', '三十'
];

/** 时辰序数：子 1 … 亥 12（23:00–00:59 为子时） */
export function hourBranchOrdinal(hour) {
  return Math.floor(((hour + 1) % 24) / 2) + 1;
}

/** 地支序数：子 1 … 亥 12 */
export function branchOrdinal(branch) {
  return BRANCHES.indexOf(branch) + 1;
}
